import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { householdKeys } from '../households/keys.ts'
import { noteKeys } from '../notes/keys.ts'
import { peopleKeys } from '../people/keys.ts'
import { tagKeys } from '../tags/keys.ts'
import { mergeKeys } from './keys.ts'

/**
 * Merge hooks. A merge moves rows across half the People module, so success
 * invalidates people, tags, notes and households wholesale rather than trying
 * to patch caches — being briefly slow beats showing a person who no longer
 * exists.
 */

export function useMergeCandidates(enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: mergeKeys.candidates(),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.merges.candidates.get({ query: { limit: 100 } })
      if (error) throw error
      return data.items
    },
  })
}

export type MergeCandidate = NonNullable<ReturnType<typeof useMergeCandidates>['data']>[number]

/** Field-by-field diff. Idle until both sides are chosen. */
export function useMergePreview(winnerId: string | null, loserId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: mergeKeys.preview(winnerId ?? '', loserId ?? ''),
    enabled: winnerId !== null && loserId !== null && winnerId !== loserId,
    queryFn: async () => {
      const { data, error } = await api.api.v1.merges.preview.post({
        winnerId: winnerId ?? '',
        loserId: loserId ?? '',
      })
      if (error) throw error
      return data
    },
  })
}

export type MergePreview = NonNullable<ReturnType<typeof useMergePreview>['data']>

export function useMergeHistory() {
  const api = useApi()
  return useQuery({
    queryKey: mergeKeys.history(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.merges.get({ query: { limit: 50 } })
      if (error) throw error
      return data.items
    },
  })
}

function useMergeInvalidation() {
  const queryClient = useQueryClient()
  return () => {
    for (const keys of [peopleKeys, tagKeys, noteKeys, householdKeys, mergeKeys]) {
      void queryClient.invalidateQueries({ queryKey: keys.all() })
    }
  }
}

export function useMergePeople() {
  const api = useApi()
  const invalidate = useMergeInvalidation()
  return useMutation({
    mutationFn: async (body: {
      winnerId: string
      loserId: string
      fields?: Record<string, 'winner' | 'loser'>
    }) => {
      const { data, error } = await api.api.v1.merges.post(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}

export function useUndoMerge() {
  const api = useApi()
  const invalidate = useMergeInvalidation()
  return useMutation({
    mutationFn: async (mergeId: string) => {
      const { data, error } = await api.api.v1.merges({ id: mergeId }).undo.post()
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
}
